import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { Check, ChevronLeft, ChevronRight, Copy, Eye, Loader2, Mail, MessageCircle, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { appendDictated, MicButton } from "../misc/dictation";
import { VoiceRecorder, voiceRecordingSupported, type VoiceAttachment } from "../misc/VoiceRecorder";
import { StorageAudio } from "../misc/storage";
import { AudioPill } from "../misc/AudioPill";
import { WhatsAppSend } from "./WhatsAppSend";
import { RelativeDate } from "../misc/RelativeDate";
import { useCompanyBlock } from "../companies/ContactBlock";
import { BouquetIcon } from "../misc/BouquetIcon";
import type { Contact } from "../types";
import { isWhatsAppCapable } from "../misc/phone";

// LDF: Digitaler Blumengruß an genau diesen Kontakt. Absender = eingeloggte Person.
// Die Karte ist dieselbe wie auf lovelydayflowers.de – Öffnungen werden dort gezählt und hier angezeigt.

type Occasion = "dank" | "weihnachten" | "neujahr" | "geburtstag" | "jubilaeum" | "einfach";
const OCCASIONS: [Occasion, string][] = [
  ["dank", "Dank"],
  ["weihnachten", "Weihnachten"],
  ["neujahr", "Neujahr"],
  ["geburtstag", "Geburtstag"],
  ["jubilaeum", "Jubiläum"],
  ["einfach", "Einfach so"],
];

type Song = { title: string; artist: string; artwork: string; preview: string; link: string };

const LAST_MUSIC_KEY = "ldf.greeting.last-music";
const readLastMusic = (): Song | null => {
  try {
    const song = JSON.parse(localStorage.getItem(LAST_MUSIC_KEY) ?? "null") as Song | null;
    return song?.preview ? song : null;
  } catch {
    return null;
  }
};
const saveLastMusic = (song: Song) => {
  try {
    localStorage.setItem(LAST_MUSIC_KEY, JSON.stringify(song));
  } catch {
    // Speicher nicht verfügbar
  }
};
type Bouquet = { hash: string; image_path: string; items: unknown };
type Greeting = {
  id: number;
  card_id: string;
  occasion: string | null;
  channel: string;
  email: string | null;
  created_at: string;
  sender: string | null;
  image_path: string | null;
  opened_count: number;
  first_opened_at: string | null;
  last_opened_at: string | null;
  status: string;
  short_code: string | null;
  whatsapp: boolean | null;
  scheduled_at: string | null;
  sent_at: string | null;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const bouquetUrl = (path: string) => `${SUPABASE_URL}/storage/v1/object/public/gallery/${path}`;
// Kurzlink /k/<code> + Versandweg-Buchstabe (m E-Mail, w WhatsApp, l Link); ältere Links ohne Code: ?id=…&via=
const withVia = (url: string, via: "w" | "l") => (url.includes("/k/") ? `${url}${via}` : `${url}&via=${via}`);
// team=1: Aufruf aus dem CRM zählt nicht als Öffnen durch den Empfänger (und markiert dieses Gerät als Team-Gerät)
const teamUrl = (url: string) => `${url}${url.includes("?") ? "&" : "?"}team=1`;
const cardUrl = (g: { card_id: string; short_code?: string | null }) =>
  teamUrl(g.short_code ? `https://lovelydayflowers.de/k/${g.short_code}` : `https://lovelydayflowers.de/karte?id=${g.card_id}`);

const whatsappHref = (number: string, text: string) => {
  let digits = number.replace(/\(0\)/g, "").replace(/[^\d+]/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("+")) digits = digits.slice(1);
  else if (digits.startsWith("0")) digits = `49${digits.slice(1)}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};
const isMobileNumber = isWhatsAppCapable;

// Anrede: „Hallo Vorname,“ (du) oder „Liebe Frau / Lieber Herr Nachname,“ (Sie) – danach Absatz
type Salutation = "du" | "frau" | "herr";
const salutationLine = (s: Salutation, firstName: string, lastName: string, title?: string | null) => {
  const last = [title, lastName || firstName].filter(Boolean).join(" ");
  if (s === "frau") return `Liebe Frau ${last},`;
  if (s === "herr") return `Lieber Herr ${last},`;
  return `Hallo ${firstName || lastName},`;
};

const template = (occasion: Occasion, salutation: Salutation, firstName: string, lastName: string, title: string | null | undefined, event: string | null, sender: string) => {
  const formal = salutation !== "du";
  const hello = salutationLine(salutation, firstName, lastName, title);
  const you = formal ? { d: "Ihnen", g: "Ihr", a: "Sie" } : { d: "dir", g: "dein", a: "dich" };
  const body: Record<Occasion, string> = {
    dank: event
      ? `vielen Dank für die schöne Zusammenarbeit bei „${event}“! Es war uns eine große Freude, dafür die Blumen zu gestalten.`
      : `vielen Dank für die schöne Zusammenarbeit! Es war uns eine große Freude.`,
    weihnachten: `wir wünschen ${you.d} ein frohes, blühendes Weihnachtsfest und ein paar ruhige Tage. Danke für ${you.g === "Ihr" ? "Ihr" : "dein"} Vertrauen in diesem Jahr!`,
    neujahr: `für das neue Jahr wünschen wir ${you.d} alles Gute – und viele Anlässe, die mit Blumen noch schöner werden.`,
    geburtstag: `herzlichen Glückwunsch zum Geburtstag! Ein kleiner Strauß von uns – wir wünschen ${you.d} einen wunderbaren Tag.`,
    jubilaeum: `herzlichen Glückwunsch zum Jubiläum! Wir freuen uns mit ${you.d} und schicken ein paar Blumen.`,
    einfach: `einfach so ein kleiner Blumengruß von uns – wir haben gerade an ${you.a} gedacht.`,
  };
  return `${hello}\n\n${body[occasion]}\n\nHerzliche Grüße\n${sender}\nLovely Day Flowers`;
};

export const GreetingButton = ({ record, variant = "button" }: { record: Contact; variant?: "button" | "tile" }) => {
  const [open, setOpen] = useState(false);
  const blocked = !!useCompanyBlock(record.company_id);
  if (blocked) return null;
  return (
    <>
      {variant === "tile" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-12 w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-left text-sm active:bg-muted cursor-pointer"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300">
            <BouquetIcon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block font-medium">Blumengruß senden</span>
            <span className="block truncate text-xs text-muted-foreground">Digitale Karte mit Strauß</span>
          </span>
        </button>
      ) : (
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setOpen(true)}>
          <BouquetIcon className="h-4 w-4" /> Blumengruß
        </Button>
      )}
      {open && <GreetingDialog record={record} open={open} onOpenChange={setOpen} />}
    </>
  );
};

const GreetingDialog = ({ record, open, onOpenChange }: { record: Contact; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const senderName = identity?.fullName ?? "";
  const emails = (record.email_jsonb ?? []).map((e) => e?.email).filter(Boolean) as string[];
  const mobiles = (record.phone_jsonb ?? []).map((p) => p?.number).filter((n): n is string => !!n && isMobileNumber(n));

  const [occasion, setOccasion] = useState<Occasion>("dank");
  const [salutation, setSalutation] = useState<Salutation>(record.gender === "male" ? "herr" : "frau");
  const formal = salutation !== "du";
  const [bouquet, setBouquet] = useState<Bouquet | null>(null);
  const [message, setMessage] = useState("");
  const [touched, setTouched] = useState(false);
  const [voice, setVoice] = useState<VoiceAttachment | null>(null);
  // „beides“: erst E-Mail, danach WhatsApp mit demselben Link
  // E-Mail und WhatsApp einzeln an- und abwählbar; beides aus = nur Link kopieren
  const [sendEmail, setSendEmail] = useState(emails.length > 0);
  const [sendWhatsapp, setSendWhatsapp] = useState(mobiles.length > 0);
  const channel: "email" | "whatsapp" | "beides" | "link" =
    sendEmail && sendWhatsapp ? "beides" : sendEmail ? "email" : sendWhatsapp ? "whatsapp" : "link";
  const usesEmail = channel === "email" || channel === "beides";
  const usesWhatsapp = channel === "whatsapp" || channel === "beides";
  // Mehrere Adressen: bewusst auswählen lassen, keine Vorauswahl
  const [email, setEmail] = useState(emails.length === 1 ? emails[0] : "");
  // Zuletzt verwendete Musik wird vorgeschlagen (lässt sich ändern oder entfernen)
  const [music, setMusic] = useState<Song | null>(readLastMusic);
  const [phone, setPhone] = useState(mobiles[0] ?? "");
  const [consent, setConsent] = useState(false);
  // Versandzeit: sofort oder zu einem festen Zeitpunkt (nur E-Mail)
  const [timing, setTiming] = useState<"sofort" | "spaeter">("sofort");
  const [sendAt, setSendAt] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ url: string; scheduled?: boolean } | null>(null);
  const [envelope, setEnvelope] = useState<"luftpost" | "klassisch">("luftpost");
  const stripRef = useRef<HTMLDivElement>(null);
  const [recordingVoice, setRecordingVoice] = useState(false);

  const { data: context } = useQuery({
    queryKey: ["greeting-context", record.id],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [{ data: summary }, { data: deals }] = await Promise.all([
        supabase.from("contacts_summary").select("had_contact,company_name,company_private").eq("id", record.id).maybeSingle(),
        supabase.from("deals").select("name,event_date").contains("contact_ids", [record.id]).order("event_date", { ascending: false, nullsFirst: false }).limit(1),
      ]);
      return {
        isCustomer: !!summary?.had_contact,
        company: summary?.company_private ? null : ((summary?.company_name as string | undefined) ?? null),
        lastEvent: (deals?.[0]?.name as string | undefined) ?? null,
      };
    },
  });

  const { data: bouquets } = useQuery({
    queryKey: ["greeting-bouquets"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("bouquets")
        .select("hash,image_path,items")
        .eq("visible", true)
        .order("created_at", { ascending: false })
        .limit(40);
      return (data ?? []) as Bouquet[];
    },
  });

  useEffect(() => {
    if (!bouquet && bouquets?.length) setBouquet(bouquets[0]);
  }, [bouquets, bouquet]);

  const suggestion = useMemo(
    () => template(occasion, salutation, record.first_name ?? "", record.last_name ?? "", record.academic_title, context?.lastEvent ?? null, senderName),
    [occasion, salutation, record.first_name, record.last_name, record.academic_title, context?.lastEvent, senderName],
  );
  // Vorschlag folgt Anlass/Anrede, bis selbst etwas geändert wurde
  useEffect(() => {
    if (!touched) setMessage(suggestion);
  }, [suggestion, touched]);

  const needsConsent = usesEmail && context && !context.isCustomer;
  // Sprachnachricht muss erst als MP3 vorliegen (spielt überall) – solange ist Senden gesperrt
  // Liefert den abspielbaren Pfad (MP3 bzw. m4a), sobald die Aufnahme vorbereitet ist – sonst null
  const { data: voicePlayable } = useQuery({
    queryKey: ["greeting-voice-ready", voice?.path],
    enabled: !!voice,
    refetchInterval: (query) => (query.state.data ? false : 1500),
    queryFn: async (): Promise<string | null> => {
      if (!voice) return null;
      const { data } = await getSupabaseClient()
        .from("voice_transcriptions")
        .select("mp3_path,status,mime_type")
        .eq("audio_path", voice.path)
        .maybeSingle();
      if (data?.mp3_path) return data.mp3_path;
      // m4a (iPhone) spielt auch ohne Umwandlung überall
      if (data && !/webm|ogg/.test(data.mime_type ?? "") && data.status !== "wartet" && data.status !== "laeuft") return voice.path;
      return null;
    },
  });
  const voiceReady = !!voicePlayable;
  const voicePending = !!voice && !voiceReady;
  const sendAtDate = timing === "spaeter" && sendAt ? new Date(sendAt) : null;
  const scheduleValid = timing === "sofort" || channel !== "email" || (!!sendAtDate && sendAtDate.getTime() > Date.now() + 2 * 60 * 1000);
  const whenLabel = sendAtDate ? sendAtDate.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "";
  const canSend =
    !!bouquet &&
    scheduleValid &&
    !voicePending &&
    (message.trim() || voice) &&
    (!usesEmail || (email && (!needsConsent || consent))) &&
    (!usesWhatsapp || phone);

  const send = async () => {
    if (!bouquet) return;
    setSending(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke<{ ok: boolean; url: string; scheduled?: boolean; message?: string }>("greeting", {
        method: "POST",
        body: {
          contact_id: record.id,
          image_path: bouquet.image_path,
          items: bouquet.items,
          message: message.trim(),
          recipient: [record.first_name, record.last_name].filter(Boolean).join(" "),
          voice_path: voice?.path ?? null,
          occasion,
          channel: channel === "beides" ? "email" : channel,
          also_whatsapp: channel === "beides",
          email: usesEmail ? email : undefined,
          formal,
          consent,
          envelope,
          salutation,
          music,
          send_at: channel === "email" && timing === "spaeter" && sendAtDate ? sendAtDate.toISOString() : null,
        },
      });
      if (error) {
        const detail = await (error as { context?: Response }).context?.json?.().catch(() => null);
        throw new Error(detail?.message ?? error.message);
      }
      if (!data?.url) throw new Error("Keine Antwort vom Server");
      queryClient.invalidateQueries({ queryKey: ["greetings", record.id] });
      queryClient.invalidateQueries({ queryKey: ["contact_notes"] });
      // WhatsApp öffnet der Nutzer im Erfolgsfenster mit einem Tipp (Browser blockieren automatisches Öffnen nach dem Senden)
      if (channel === "link") {
        await navigator.clipboard?.writeText(withVia(data.url, "l")).catch(() => {});
      }
      notify(data.scheduled ? `Blumengruß an ${email} geplant für ${whenLabel} Uhr` : usesEmail ? `Blumengruß an ${email} gesendet` : usesWhatsapp ? "Karte erstellt – jetzt in WhatsApp senden" : "Link kopiert", {
        type: "success",
      });
      if (music) saveLastMusic(music);
      setSent({ url: data.url, scheduled: !!data.scheduled });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const chip = (active: boolean) =>
    `rounded-full px-3 py-1 text-sm cursor-pointer ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Breiter als Standard (Tablet/Desktop) und eine Spalte, die nie breiter als das Fenster wird
        className="grid-cols-[minmax(0,1fr)] sm:max-w-2xl"
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BouquetIcon className="h-5 w-5" /> Blumengruß an {[record.first_name, record.last_name].filter(Boolean).join(" ")}
          </DialogTitle>
          <DialogDescription>Von {senderName || "dir"} · Einzelversand an diesen Kontakt</DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-3 text-sm">
            <p className="flex items-center gap-2 font-medium">
              <Check className="h-4 w-4 text-green-600" />{" "}
              {sent.scheduled
                ? `Geplant für ${whenLabel} Uhr – wird dann automatisch verschickt. Beim Kontakt unter „Blumengrüße“ lässt es sich noch stornieren.`
                : "Gesendet – beim Kontakt als Notiz vermerkt. Sobald die Karte geöffnet wird, steht es hier."}
            </p>
            {usesWhatsapp && !sent.scheduled && (
              <WhatsAppSend
                href={whatsappHref(phone, `${formal ? "Ich habe Ihnen" : "Ich habe dir"} einen Brief geschickt ✉️ ${withVia(sent.url, "w")}`)}
                phone={phone}
                senderName={senderName}
                salesId={identity?.id}
              />
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => window.open(teamUrl(sent.url), "_blank", "noopener")}>
                <Eye className="h-4 w-4" /> Karte ansehen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard?.writeText(withVia(sent.url, "l"));
                  notify("Link kopiert – beim Einfügen in WhatsApp warten, bis die Vorschau erscheint, dann senden", { type: "info" });
                }}
              >
                <Copy className="h-4 w-4" /> Link kopieren
              </Button>
              <Button size="sm" onClick={() => onOpenChange(false)}>
                Fertig
              </Button>
            </div>
          </div>
        ) : (
          <div className="min-w-0 space-y-4 [&>section]:min-w-0">
            <section className="space-y-2">
              <h3 className="text-sm font-medium">Anlass</h3>
              <div className="flex flex-wrap gap-1.5">
                {OCCASIONS.map(([value, label]) => (
                  <button key={value} type="button" className={chip(occasion === value)} onClick={() => setOccasion(value)}>
                    {label}
                  </button>
                ))}
              </div>
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Strauß</h3>
                <div className="flex gap-1">
                  <button type="button" aria-label="Weitere Sträuße links" onClick={() => stripRef.current?.scrollBy({ left: -320, behavior: "smooth" })} className="rounded-full border border-border p-1 hover:bg-muted cursor-pointer">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button type="button" aria-label="Weitere Sträuße rechts" onClick={() => stripRef.current?.scrollBy({ left: 320, behavior: "smooth" })} className="rounded-full border border-border p-1 hover:bg-muted cursor-pointer">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div ref={stripRef} className="flex max-w-full snap-x gap-2 overflow-x-auto overscroll-x-contain pb-2 [touch-action:pan-x]" data-no-swipe>
                {(bouquets ?? []).map((b) => (
                  <button
                    key={b.hash}
                    type="button"
                    onClick={() => setBouquet(b)}
                    className={`relative h-24 w-24 shrink-0 snap-start overflow-hidden rounded-lg border-2 cursor-pointer ${bouquet?.hash === b.hash ? "border-primary" : "border-transparent"}`}
                    aria-pressed={bouquet?.hash === b.hash}
                  >
                    <img src={bouquetUrl(b.image_path)} alt="Strauß" loading="lazy" className="h-full w-full object-cover" />
                    {bouquet?.hash === b.hash && (
                      <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                ))}
                {!bouquets && <Loader2 className="m-4 h-5 w-5 animate-spin text-muted-foreground" />}
              </div>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium">Umschlag</h3>
              <div className="grid grid-cols-2 gap-2">
                <EnvelopeOption
                  active={envelope === "luftpost"}
                  onClick={() => setEnvelope("luftpost")}
                  label="Luftpost"
                  hint="Mit Anschrift und Poststempel"
                  record={record}
                  kind="luftpost"
                  salutation={salutation}
                  company={context?.company ?? null}
                />
                <EnvelopeOption
                  active={envelope === "klassisch"}
                  onClick={() => setEnvelope("klassisch")}
                  label="Klassisch"
                  hint="Name in Schreibschrift"
                  record={record}
                  kind="klassisch"
                  salutation={salutation}
                  company={context?.company ?? null}
                />
              </div>
            </section>

            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Text</h3>
                <div className="flex items-center gap-2">
                  <select
                    aria-label="Anrede"
                    value={salutation}
                    // Entwurf folgt der Anrede; ein selbst geänderter Text bleibt unverändert
                    onChange={(e) => setSalutation(e.target.value as Salutation)}
                    className="h-8 max-w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {(record.gender !== "male" || !record.gender) && (
                      <option value="frau">{salutationLine("frau", record.first_name ?? "", record.last_name ?? "", record.academic_title)}</option>
                    )}
                    {(record.gender !== "female" || !record.gender) && (
                      <option value="herr">{salutationLine("herr", record.first_name ?? "", record.last_name ?? "", record.academic_title)}</option>
                    )}
                    <option value="du">{salutationLine("du", record.first_name ?? "", record.last_name ?? "")}</option>
                  </select>
                  <MicButton title="Text diktieren" onText={(t) => { setTouched(true); setMessage((v) => appendDictated(v, t)); }} />
                </div>
              </div>
              <textarea
                value={message}
                onChange={(e) => {
                  setTouched(true);
                  setMessage(e.target.value);
                }}
                rows={7}
                maxLength={1500}
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
              {touched && (
                <button type="button" className="text-xs text-muted-foreground underline cursor-pointer" onClick={() => setTouched(false)}>
                  Vorschlag wiederherstellen
                </button>
              )}
            </section>

            {voiceRecordingSupported() && (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">Sprachnachricht (optional)</h3>
                {voice ? (
                  <div className="flex items-center gap-3">
                    {voiceReady ? (
                      <StorageAudio file={{ path: voicePlayable, type: "audio/mpeg" }} title={voice.title} />
                    ) : (
                      // Zwischenstatus: Aufnahme wird noch umgewandelt – Abspielen erst danach möglich
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="text-xs text-muted-foreground">🎙️ {voice.title}</span>
                        <div className="flex w-full max-w-sm items-center gap-2 rounded-full border border-border bg-muted/60 py-1 pl-1 pr-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted-foreground/30 text-white">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </span>
                          <span className="text-xs text-muted-foreground">Wird vorbereitet … gleich abspielbar</span>
                        </div>
                      </div>
                    )}
                    <button type="button" className="text-xs text-muted-foreground underline cursor-pointer" onClick={() => setVoice(null)}>
                      entfernen
                    </button>
                  </div>
                ) : (
                  <div className="flex min-w-0 items-center gap-3">
                    <VoiceRecorder
                      title="Persönliche Sprachnachricht aufnehmen"
                      onAudio={setVoice}
                      onText={() => {}}
                      onActiveChange={setRecordingVoice}
                    />
                    {!recordingVoice && <span className="min-w-0 text-xs text-muted-foreground">Spielt auf der Karte ab – wird nicht verschriftlicht.</span>}
                  </div>
                )}
              </section>
            )}

            <section className="space-y-2">
              <h3 className="text-sm font-medium">Musik (optional)</h3>
              <MusicPicker value={music} onChange={setMusic} />
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium">Versand</h3>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  disabled={!emails.length}
                  aria-pressed={sendEmail}
                  title={!emails.length ? "Keine E-Mail-Adresse hinterlegt" : undefined}
                  className={`${chip(sendEmail)} disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1`}
                  onClick={() => setSendEmail(!sendEmail)}
                >
                  {sendEmail ? <Check className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />} E-Mail
                </button>
                <button
                  type="button"
                  disabled={!mobiles.length}
                  aria-pressed={sendWhatsapp}
                  title={!mobiles.length ? "Keine Handynummer hinterlegt" : undefined}
                  className={`${chip(sendWhatsapp)} disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1`}
                  onClick={() => {
                    setSendWhatsapp(!sendWhatsapp);
                    if (!sendWhatsapp) setTiming("sofort");
                  }}
                >
                  {sendWhatsapp ? <Check className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />} WhatsApp
                </button>
                <button
                  type="button"
                  aria-pressed={channel === "link"}
                  className={`${chip(channel === "link")} inline-flex items-center gap-1`}
                  onClick={() => {
                    setSendEmail(false);
                    setSendWhatsapp(false);
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> Nur Link kopieren
                </button>
              </div>
              {usesEmail && emails.length > 1 && (
                <fieldset className={`space-y-1 rounded-md border p-2 ${email ? "border-border" : "border-amber-400 bg-amber-50/60 dark:bg-amber-950/40"}`}>
                  <legend className="px-1 text-xs font-medium">An welche Adresse? Bitte auswählen</legend>
                  {emails.map((e) => (
                    <label key={e} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input type="radio" name="greeting-email" checked={email === e} onChange={() => setEmail(e)} />
                      <span className="min-w-0 truncate">{e}</span>
                    </label>
                  ))}
                </fieldset>
              )}
              {usesEmail && emails.length === 1 && <p className="text-xs text-muted-foreground">An {email} · Antworten gehen an deine E-Mail-Adresse</p>}
              {usesWhatsapp && mobiles.length > 1 && (
                <select value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  {mobiles.map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              )}
              {channel === "beides" && (
                <p className="text-xs text-muted-foreground">Beides gewählt: Erst geht die E-Mail raus, danach öffnest du mit einem Tipp WhatsApp – Text und Link sind schon vorbereitet. Planen geht nur mit E-Mail allein.</p>
              )}
              {channel === "email" && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Versandzeit:</span>
                    <button type="button" className={chip(timing === "sofort")} onClick={() => setTiming("sofort")}>
                      Sofort
                    </button>
                    <button type="button" className={chip(timing === "spaeter")} onClick={() => setTiming("spaeter")}>
                      Später
                    </button>
                  </div>
                  {timing === "spaeter" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        value={sendAt}
                        min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000 + 5 * 60000).toISOString().slice(0, 16)}
                        onChange={(e) => setSendAt(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      />
                      {sendAt && !scheduleValid && <span className="text-xs text-destructive">Bitte einen Zeitpunkt in der Zukunft wählen</span>}
                    </div>
                  )}
                </div>
              )}
              {needsConsent && (
                <label className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                  <span>
                    Noch kein Kunde. Per E-Mail nur, wenn eine <strong>persönliche Beziehung</strong> besteht oder die Person zugestimmt hat (§ 7 UWG). Sonst lieber WhatsApp oder Link.
                  </span>
                </label>
              )}
            </section>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                disabled={!canSend || sending}
                onClick={send}
                className={`gap-1 ${canSend && !sending ? "bg-[#25A244] text-white hover:bg-[#1f8f3b]" : ""}`}
              >
                {sending || voicePending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {voicePending ? "Sprachnachricht wird vorbereitet …" : usesEmail ? (!email ? "Adresse auswählen" : channel === "beides" ? "E-Mail senden, dann WhatsApp" : timing === "spaeter" ? (sendAtDate && scheduleValid ? `Für ${whenLabel} Uhr planen` : "Zeitpunkt wählen") : `An ${email} senden`) : usesWhatsapp ? "Mit WhatsApp senden" : "Karte erstellen & Link kopieren"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Gesendete Blumengrüße eines Kontakts mit Öffnungs-Status */
export const GreetingsList = ({ contactId }: { contactId: number | string }) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const cancel = async (id: number) => {
    const { error } = await getSupabaseClient().rpc("cancel_greeting", { p_id: id });
    if (error) return notify("Stornieren fehlgeschlagen", { type: "error" });
    notify("Geplanter Blumengruß storniert", { type: "success" });
    queryClient.invalidateQueries({ queryKey: ["greetings", contactId] });
  };
  const { data } = useQuery({
    queryKey: ["greetings", contactId],
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("greetings_for_contact", { p_contact_id: Number(contactId) });
      return (data ?? []) as Greeting[];
    },
  });
  if (!data?.length) return null;
  return (
    <ul className="space-y-2">
      {data.map((g) => (
        <li key={g.id} className="flex items-center gap-2 text-sm">
          {g.image_path && <img src={bouquetUrl(g.image_path)} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />}
          <div className="min-w-0 flex-1">
            <a href={cardUrl(g)} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
              {OCCASIONS.find(([v]) => v === g.occasion)?.[1] ?? "Blumengruß"}
            </a>{" "}
            <span className="text-xs text-muted-foreground">
              · <RelativeDate date={g.created_at} /> · {g.channel === "email" ? (g.whatsapp ? "E-Mail + WhatsApp" : "E-Mail") : g.channel === "whatsapp" ? "WhatsApp" : "Link"}
            </span>
            <div className={`text-xs ${g.opened_count ? "text-green-700 dark:text-green-400" : g.status === "geplant" ? "text-amber-700 dark:text-amber-400" : g.status === "fehler" ? "text-destructive" : "text-muted-foreground"}`}>
              {g.status === "geplant" ? (
                <>
                  ⏰ Geplant für {new Date(g.scheduled_at!).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })} Uhr ·{" "}
                  <button type="button" onClick={() => cancel(g.id)} className="underline cursor-pointer">
                    stornieren
                  </button>
                </>
              ) : g.status === "storniert" ? (
                "Storniert"
              ) : g.status === "fehler" ? (
                "Versand fehlgeschlagen"
              ) : g.opened_count ? (
                <>
                  Vom Empfänger geöffnet <RelativeDate date={g.first_opened_at!} />
                  {g.opened_count > 1 ? ` · ${g.opened_count}×` : ""}
                </>
              ) : (
                "Noch nicht geöffnet"
              )}
            </div>
            {g.status === "gesendet" && <OpensHistory greetingId={g.id} />}
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Kleine Vorschau der beiden Umschläge */
const EnvelopeOption = ({
  active,
  onClick,
  label,
  hint,
  record,
  kind,
  salutation: chosen,
  company,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
  record: Contact;
  kind: "luftpost" | "klassisch";
  salutation: Salutation;
  company: string | null;
}) => {
  const name = [record.academic_title, record.first_name, record.last_name].filter(Boolean).join(" ");
  const salutation = chosen === "frau" ? "Frau" : chosen === "herr" ? "Herrn" : record.gender === "female" ? "Frau" : record.gender === "male" ? "Herrn" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-w-0 rounded-lg border-2 p-1.5 text-left cursor-pointer ${active ? "border-primary" : "border-transparent hover:border-border"}`}
    >
      <div
        className="relative aspect-[3/2] w-full overflow-hidden rounded"
        style={
          kind === "luftpost"
            ? { background: "repeating-linear-gradient(-45deg,#d8343f 0 8px,#fdfbf6 8px 11px,#2346b8 11px 19px,#fdfbf6 19px 22px)" }
            : { background: "#fbf4f0", border: "1px solid #ddbbaf" }
        }
      >
        {kind === "luftpost" ? (
          <div className="absolute inset-[5px] rounded-[2px] bg-[#fdfbf6]">
            <span className="absolute left-1.5 top-1.5 rounded-[2px] bg-[#2346b8] px-1 text-[7px] font-bold leading-tight text-white">LUFTPOST</span>
            <span className="absolute right-1.5 top-1.5 h-4 w-3.5 border border-dashed border-stone-300 bg-[#f7e6df]" />
            <span className="absolute right-6 top-1 h-5 w-5 rounded-full border border-[#2346b8]/60" />
            <div className="absolute bottom-[18%] left-1/2 right-1 font-mono text-[7px] leading-tight text-[#1f2a44]">
              {salutation && <div className="truncate">{salutation}</div>}
              <div className="truncate font-bold">{name}</div>
              {company && <div className="truncate opacity-80">{company}</div>}
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center px-2 text-center text-lg leading-none text-stone-800" style={{ fontFamily: "'Great Vibes', 'Snell Roundhand', cursive" }}>
            {[record.first_name, record.last_name].filter(Boolean).join(" ")}
          </div>
        )}
      </div>
      <div className="mt-1 text-sm font-medium">{label}</div>
      <div className="truncate text-xs text-muted-foreground">{hint}</div>
    </button>
  );
};

/** Song-Suche wie auf der Website (iTunes, 30-Sekunden-Vorschau spielt auf der Karte beim Auspacken) */
const MusicPicker = ({ value, onChange }: { value: Song | null; onChange: (song: Song | null) => void }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Song[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      const { data } = await getSupabaseClient().functions.invoke<{ results: Song[] }>("music-search", { body: { q } });
      if (active) {
        setResults(data?.results ?? []);
        setLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  if (value) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-md border border-border p-2">
        {value.artwork && <img src={value.artwork} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />}
        <div className="min-w-0 flex-1 text-sm">
          <div className="truncate font-medium">{value.title}</div>
          <div className="truncate text-xs text-muted-foreground">{value.artist}</div>
        </div>
        <AudioPill src={value.preview} className="w-full sm:w-60" />
        <button type="button" onClick={() => onChange(null)} className="text-xs text-muted-foreground underline cursor-pointer">
          entfernen
        </button>
      </div>
    );
  }
  return (
    <div className="min-w-0 space-y-1">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Song oder Interpret suchen …"
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
      />
      {loading && <p className="text-xs text-muted-foreground">Sucht …</p>}
      {results.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {results.map((song) => (
            <li key={song.preview}>
              <button
                type="button"
                onClick={() => {
                  onChange(song);
                  setQuery("");
                  setResults([]);
                }}
                className="flex w-full min-w-0 items-center gap-2 p-2 text-left text-sm hover:bg-muted cursor-pointer"
              >
                {song.artwork && <img src={song.artwork} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />}
                <span className="min-w-0">
                  <span className="block truncate font-medium">{song.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{song.artist}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Zugriffsverlauf: Balken pro Tag (letzte 14 Tage) und Liste der Aufrufe – Team-Aufrufe grau */
const OpensHistory = ({ greetingId }: { greetingId: number }) => {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["greeting-opens", greetingId],
    enabled: open,
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("greeting_opens", { p_greeting_id: greetingId });
      return (data ?? []) as { opened_at: string; team: boolean; device: string | null; ip_prefix: string | null; via: string | null }[];
    },
  });
  const days = useMemo(() => {
    const list: { key: string; label: string; guest: number; team: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      list.push({ key: d.toDateString(), label: d.toLocaleDateString("de-DE", { day: "numeric", month: "numeric" }), guest: 0, team: 0 });
    }
    for (const o of data ?? []) {
      const day = list.find((x) => x.key === new Date(o.opened_at).toDateString());
      if (day) day[o.team ? "team" : "guest"]++;
    }
    return list;
  }, [data]);
  const max = Math.max(1, ...days.map((d) => d.guest + d.team));
  // Netze, aus denen das Team selbst geöffnet hat – gleiche gekürzte IP bei „Empfänger“ ist verdächtig
  const teamNets = new Set((data ?? []).filter((o) => o.team && o.ip_prefix).map((o) => o.ip_prefix));
  const deviceLabel = (d: string | null) => (d === "handy" ? "📱 Handy" : d === "tablet" ? "📱 Tablet" : d === "computer" ? "💻 Computer" : "");
  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen(!open)} className="text-xs text-muted-foreground underline cursor-pointer">
        {open ? "Verlauf ausblenden" : "Zugriffsverlauf"}
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-md border border-border p-2">
          <div className="flex h-16 items-end gap-1" aria-label="Aufrufe der letzten 14 Tage">
            {days.map((d) => (
              <div key={d.key} className="flex flex-1 flex-col items-center gap-0.5" title={`${d.label}: ${d.guest} Empfänger, ${d.team} Team`}>
                <div className="flex w-full flex-col-reverse overflow-hidden rounded-sm" style={{ height: `${((d.guest + d.team) / max) * 48}px` }}>
                  <div className="bg-primary" style={{ height: `${(d.guest / Math.max(1, d.guest + d.team)) * 100}%` }} />
                  <div className="bg-muted-foreground/30" style={{ height: `${(d.team / Math.max(1, d.guest + d.team)) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{days[0].label}</span>
            <span>
              <span className="inline-block h-2 w-2 rounded-sm bg-primary align-middle" /> Empfänger ·{" "}
              <span className="inline-block h-2 w-2 rounded-sm bg-muted-foreground/30 align-middle" /> Team
            </span>
            <span>heute</span>
          </div>
          {data && data.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Aufrufe seit Beginn der Aufzeichnung.</p>}
          {data && data.length > 0 && (
            <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs">
              {[...data].reverse().map((o) => (
                <li key={o.opened_at} className={o.team ? "text-muted-foreground" : ""}>
                  {new Date(o.opened_at).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} Uhr
                  {o.team ? " · Team" : " · Empfänger"}
                  {o.via && ` · über ${o.via === "mail" ? "E-Mail" : o.via === "whatsapp" ? "WhatsApp" : "Link"}`}
                  {o.device && ` · ${deviceLabel(o.device)}`}
                  {o.ip_prefix && <span className="text-muted-foreground"> · Netz {o.ip_prefix}</span>}
                  {!o.team && o.ip_prefix && teamNets.has(o.ip_prefix) && <span className="text-amber-700 dark:text-amber-400"> · gleiches Netz wie Team</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
